import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { api, money, type AttemptDetail } from "../api";
import { Artifact } from "../components/Artifact";
import { listState } from "../components/QueryState";
import { Status } from "./RunList";

/**
 * One run, its attempts, and the evidence behind each.
 *
 * The cost is shown as three buckets rather than one number, because they do
 * not sum the way a single figure implies: triage scores every issue in the
 * window including the rejected ones, and only the third bucket is the
 * fix-review loop.
 */
export function RunDetailView() {
  const { runId = "" } = useParams();
  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.run(runId),
    refetchInterval: (query) =>
      query.state.data?.run.is_terminal ? false : 3_000,
  });

  if (run.isPending) return <p className="muted">Loading run…</p>;
  if (run.error)
    return <p className="error">Could not load run: {run.error.message}</p>;

  const { run: summary, cost, attempts } = run.data;

  return (
    <article>
      <p>
        <Link to="/">← All runs</Link>
      </p>
      <h1>
        {summary.repo} <span className="muted">{summary.run_date}</span>
      </h1>
      <p>
        <Status value={summary.status} />{" "}
        <span className="muted">{summary.reason ?? "no reason recorded"}</span>
      </p>

      <dl className="cost">
        <div>
          <dt>Triage</dt>
          <dd>{money(cost.triage)}</dd>
        </div>
        <div>
          <dt>Diagnosis</dt>
          <dd>{money(cost.attempt_outside_iteration)}</dd>
        </div>
        <div>
          <dt>Fix–review loop</dt>
          <dd>{money(cost.per_iteration)}</dd>
        </div>
        <div className="total">
          <dt>Total</dt>
          <dd>{money(cost.total)}</dd>
        </div>
      </dl>
      {cost.total === null ? (
        <p className="muted">
          No spend is attributed to this run. That is not the same as costing
          nothing — calls recorded before attribution existed cannot be traced
          back to their run.
        </p>
      ) : null}

      <h2>Attempts</h2>
      {attempts.map((attempt) => (
        <Attempt key={attempt.id} attempt={attempt} />
      ))}
    </article>
  );
}

function Attempt({ attempt }: { attempt: AttemptDetail }) {
  const [open, setOpen] = useState(false);
  const queries = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api.cancelAttempt(attempt.id),
    // The attempt does not go terminal here — the worker does that when it
    // reaches its next step — so the useful refresh is the run, which is what
    // shows the status changing when it does.
    onSuccess: () => queries.invalidateQueries({ queryKey: ["run"] }),
  });

  return (
    <section className="attempt">
      <header>
        <button
          type="button"
          className="disclose"
          onClick={() => setOpen(!open)}
        >
          {open ? "▾" : "▸"}
        </button>
        <code>{attempt.sentry_short_id ?? attempt.sentry_issue_id}</code>
        <Status value={attempt.status} />
        <span className="muted">{attempt.reason ?? ""}</span>
        {attempt.forced_reason ? (
          // Forced attempts are excluded from the calibration report, so
          // labelling them here keeps the two views telling the same story.
          <span className="badge">forced past {attempt.forced_reason}</span>
        ) : null}
        <span className="num">{money(attempt.cost_usd)}</span>
        {attempt.is_terminal ? null : (
          <button
            type="button"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending || cancel.isSuccess}
          >
            {cancel.isSuccess ? "Stopping…" : "Cancel"}
          </button>
        )}
      </header>
      {cancel.isSuccess ? (
        <p className="muted">
          Asked to stop. It finishes the step it is on and records what it
          actually did — so a change request it had already opened is still
          written down rather than lost.
        </p>
      ) : null}
      {cancel.error ? (
        <p className="error">Could not cancel: {cancel.error.message}</p>
      ) : null}

      {attempt.title ? <p>{attempt.title}</p> : null}
      {attempt.reason_detail ? (
        <p className="muted">{attempt.reason_detail}</p>
      ) : null}
      {attempt.change_request_url ? (
        <p>
          <a
            href={attempt.change_request_url}
            rel="noreferrer noopener"
            target="_blank"
          >
            {attempt.change_request_url}
          </a>
        </p>
      ) : null}

      {open ? <AttemptDetailPanel attempt={attempt} /> : null}
    </section>
  );
}

/**
 * What the agent did, step by step.
 *
 * Polled while the attempt is in flight and then left alone — the events are
 * append-only, so a terminal attempt's timeline cannot change.
 */
function Progress({
  attemptId,
  isTerminal,
}: {
  attemptId: string;
  isTerminal: boolean;
}) {
  const events = useQuery({
    queryKey: ["events", attemptId],
    queryFn: () => api.attemptEvents(attemptId),
    refetchInterval: isTerminal ? false : 3_000,
  });

  if (events.isPending) return <p className="muted">Loading…</p>;
  if (events.error) return <p className="error">{events.error.message}</p>;
  if (events.data.length === 0) {
    return (
      <p className="muted">
        {isTerminal
          ? // Not "predates the event log", which was a guess printed as a
            // fact: an attempt that failed while preparing — a bad branch, a
            // missing build command — reaches a terminal state before the
            // graph runs a single node, and there is nothing to have logged.
            // The reason on the attempt above says what happened.
            "No steps ran. This attempt ended before the agent started — see the reason above."
          : "Waiting for the worker to start."}
      </p>
    );
  }

  return (
    <ul className="timeline">
      {events.data.map((event, index) => (
        // eslint-disable-next-line react/no-array-index-key -- append-only, no id served
        <li key={index}>
          <span className="when">{event.at.slice(11, 19)}</span>
          <span className={`what ${event.status}`}>
            {event.label}
            {event.iteration ? ` (iteration ${event.iteration})` : ""}
            {event.status === "started" ? "…" : ""}
            {event.detail ? (
              <span className="muted"> — {event.detail}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AttemptDetailPanel({ attempt }: { attempt: AttemptDetail }) {
  // **An artifact is immutable; the *list* of them is not.** Freezing both was
  // right about the bytes and wrong about the index: a running attempt writes
  // its diagnosis, then its patch, then its review, and a frozen list showed
  // "No artifacts stored for this attempt" for the whole session — next to a
  // timeline visibly advancing through the phases that were writing them.
  //
  // So the freeze now follows the attempt, exactly as `Progress` does: poll
  // while it is in flight, stop touching it once it can no longer change. The
  // individual artifact *bodies* are fetched by `Artifact`, keyed by document
  // id, and are still cached forever — which is what the original comment was
  // actually protecting.
  const growing = {
    refetchInterval: attempt.is_terminal ? (false as const) : 5_000,
    staleTime: attempt.is_terminal ? Number.POSITIVE_INFINITY : 0,
  };
  const documents = useQuery({
    queryKey: ["documents", attempt.id],
    queryFn: () => api.documents(attempt.id),
    ...growing,
  });
  const caps = useQuery({
    queryKey: ["confidence", attempt.id],
    queryFn: () => api.confidence(attempt.id),
    ...growing,
  });
  const findings = useQuery({
    queryKey: ["findings", attempt.id],
    queryFn: () => api.findings(attempt.id),
    ...growing,
  });

  return (
    <div className="panel">
      <h3>Progress</h3>
      <Progress attemptId={attempt.id} isTerminal={attempt.is_terminal} />

      <h3>Iterations</h3>
      {attempt.iterations.length === 0 ? (
        <p className="muted">No iteration was started.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Mode</th>
              <th>Outcome</th>
              <th className="num">Blocking</th>
              <th className="num">Findings</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {attempt.iterations.map((iteration) => (
              <tr key={iteration.id}>
                <td className="num">{iteration.seq}</td>
                <td>{iteration.mode}</td>
                <td>
                  {iteration.outcome ?? <span className="warn">in flight</span>}
                </td>
                {/* Null is rendered as "not reviewed", never as 0. A crashed
                    iteration used to read as a clean, free review. */}
                <td className="num">{iteration.blocking_findings ?? "—"}</td>
                <td className="num">{iteration.total_findings ?? "—"}</td>
                <td className="num">{money(iteration.cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Confidence</h3>
      {listState(
        caps,
        "confidence checks",
        "No confidence checks recorded.",
      ) ?? (
        <ul className="caps">
          {(caps.data ?? []).map((cap) => (
            <li key={cap.check} className={cap.applied ? "applied" : ""}>
              <code>{cap.check}</code>{" "}
              {cap.applied ? (
                <strong>capped</strong>
              ) : (
                <span className="muted">clear</span>
              )}
              {cap.ceiling ? ` → ceiling ${cap.ceiling}` : ""}
              {cap.detail ? (
                <span className="muted"> — {cap.detail}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <h3>Review findings</h3>
      {listState(findings, "review findings", "No findings recorded.") ?? (
        <ul className="findings">
          {(findings.data ?? []).map((finding) => (
            <li key={finding.id}>
              <span className={finding.blocking ? "badge blocking" : "badge"}>
                {finding.severity}
              </span>{" "}
              <code>
                {finding.file_path}
                {finding.line ? `:${finding.line}` : ""}
              </code>
              <p>{finding.description}</p>
            </li>
          ))}
        </ul>
      )}

      <h3>Artifacts</h3>
      {listState(
        documents,
        "artifacts",
        "No artifacts stored for this attempt.",
      ) ??
        (documents.data ?? []).map((document) => (
          <Artifact key={document.id} document={document} />
        ))}
    </div>
  );
}
